<template>
    <div class="town-zoning-plan-map">
        <div class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2 small text-secondary">
            <span>
                <template v-if="currency.state">{{ currency.state }}</template>
                <template v-if="currency.state && map.geometrySource"> · </template>
                <template v-if="map.geometrySource">Zdroj: {{ sourceLabel(map.geometrySource) }}</template>
            </span>
            <span v-if="distinguishesPlanState">Návrhové plochy jsou ohraničené přerušovanou čarou.</span>
        </div>
        <div v-if="currency.warning" class="alert alert-warning py-2 small mb-2">
            <span class="fas fa-exclamation-triangle me-1"></span>
            {{ currency.warning }} Závazné je znění v dokumentech níže.
        </div>
        <div v-if="currency.info" class="small text-secondary mb-2">
            {{ currency.info }} Závazné je znění v dokumentech níže.
        </div>
        <div v-if="map.partialVectorization" class="alert alert-danger py-2 small mb-2">
            <span class="fas fa-lock me-1"></span>
            Vektorizace výkresu je jen „partial“ — uživatelé tuto mapu nevidí (zobrazeno jen pro admina).
        </div>
        <div v-if="proposalSummary !== null" class="small mb-2">
            <strong>Návrh plánu: {{ formatHectares(proposedHectares) }} ha</strong>
            <span class="text-secondary"> — {{ proposalSummary }}</span>
        </div>

        <div class="row g-3">
            <div class="col-lg-8">
                <div class="town-zoning-plan-map__map position-relative">
                    <LMap :center="initialCenter"
                          :zoom="13"
                          :options="{zoomSnap: 0.25, zoomControl: false, maxZoom: 21}"
                          class="h-100"
                          @ready="onMapReady">
                        <LControlLayers position="topright"></LControlLayers>
                        <MapyCzBasicLayer/>
                        <MapyCzOrthoPhotoLayer :visible="false"/>
                        <LControlZoom position="topleft"/>
                    </LMap>
                    <div v-if="zoomTooSmall" class="town-zoning-plan-map__prompt">
                        Pro zobrazení ploch přibližte mapu
                    </div>
                    <div v-if="loading" class="town-zoning-plan-map__spinner">
                        <div class="spinner-border spinner-border-sm" role="status"></div>
                    </div>
                    <button v-if="returnableViews > 0"
                            type="button"
                            class="btn btn-light btn-sm shadow-sm town-zoning-plan-map__back"
                            @click="returnToPreviousView">
                        <span class="fas fa-arrow-left me-1"></span> Zpět na předchozí výřez
                    </button>
                </div>
                <div class="d-flex flex-wrap align-items-center column-gap-3 row-gap-1 mt-2 small">
                    <span class="text-secondary">Další vrstvy:</span>
                    <div v-for="overlay in overlayLayers" :key="overlay.key" class="form-check form-check-inline me-0 mb-0">
                        <input :id="`town-zoning-plan-${overlay.key}`"
                               v-model="visibleOverlays"
                               :value="overlay.key"
                               type="checkbox"
                               class="form-check-input">
                        <label :for="`town-zoning-plan-${overlay.key}`" class="form-check-label" :title="overlay.title">{{ overlay.label }}</label>
                    </div>
                    <div class="d-flex align-items-center gap-2 ms-lg-auto">
                        <label for="town-zoning-plan-opacity" class="text-secondary text-nowrap mb-0">Průhlednost ploch</label>
                        <input id="town-zoning-plan-opacity"
                               v-model.number="fillOpacity"
                               type="range"
                               class="form-range town-zoning-plan-map__opacity"
                               min="0.1"
                               max="0.9"
                               step="0.05">
                    </div>
                </div>
            </div>

            <div class="col-lg-4">
                <div v-if="distinguishesPlanState" class="btn-group btn-group-sm w-100 mb-2" role="group" aria-label="Zobrazené plochy">
                    <button v-for="option in modeOptions"
                            :key="option.mode"
                            type="button"
                            class="btn"
                            :class="mode === option.mode ? 'btn-secondary' : 'btn-outline-secondary'"
                            @click="mode = option.mode">
                        {{ option.label }}
                    </button>
                </div>
                <p v-if="!distinguishesPlanState" class="small text-secondary mb-2">
                    Zdroj dat nerozlišuje stávající stav a návrh. Které plochy jsou rozvojové, uvádí znění
                    v dokumentech níže.
                </p>
                <p v-else-if="unknownStateHectares > 0" class="small text-secondary mb-2">
                    U {{ formatHectares(unknownStateHectares) }} ha ploch zdroj neuvádí, zda jde o stav, nebo
                    návrh; v režimech Stav a Návrh nejsou vidět.
                </p>

                <div class="d-flex justify-content-between small text-secondary px-2 mb-1">
                    <span>Legenda obce</span>
                    <span title="Podíl z ploch, které územní plán v mapě pokrývá">ha · podíl z ploch plánu</span>
                </div>
                <div class="list-group town-zoning-plan-map__legend mb-2">
                    <button v-for="row in legendRows"
                            :key="row.key"
                            type="button"
                            class="list-group-item list-group-item-action d-flex align-items-center gap-2 py-1 px-2 small"
                            :class="{'town-zoning-plan-map__legend-row--selected': selectedKeys.includes(row.key)}"
                            :title="row.title"
                            @mouseenter="hoveredKey = row.key"
                            @mouseleave="hoveredKey = null"
                            @click="toggleSelected(row.key)">
                        <span class="town-zoning-plan-map__swatch" :style="{background: row.color}"></span>
                        <span class="flex-grow-1 text-truncate">
                            <strong v-if="row.code">{{ row.code }}</strong> {{ row.name }}
                        </span>
                        <span class="text-nowrap text-secondary">{{ formatHectares(row.hectares) }} · {{ formatShare(row.hectares) }}</span>
                    </button>
                    <div v-if="legendRows.length === 0" class="list-group-item small text-secondary">
                        V tomto zobrazení nejsou žádné plochy.
                    </div>
                </div>
                <p v-if="map.annulledHectares > 0" class="d-flex align-items-center gap-2 small text-secondary px-2 mb-2">
                    <span class="town-zoning-plan-map__swatch town-zoning-plan-map__swatch--annulled" :style="{borderColor: annulledColor}"></span>
                    <span>{{ annulledLegendLabel }}: {{ formatHectares(map.annulledHectares) }} ha. {{ annulledExplanation }}</span>
                </p>
                <div class="d-flex justify-content-between small">
                    <span class="text-secondary">Kliknutím na položku ji zvýrazníte v mapě.</span>
                    <a v-if="selectedKeys.length > 0" href="#" class="text-nowrap ms-2" @click.prevent="selectedKeys = []">Zrušit výběr</a>
                </div>
            </div>
        </div>

        <div v-if="proposalSummary !== null" class="mt-3">
            <button type="button" class="btn btn-link btn-sm p-0 text-decoration-none" @click="toggleProposedAreas">
                <!-- FontAwesome swaps the icon for an SVG Vue does not see, so the wrapper turns, not the icon. -->
                <span class="town-zoning-plan-map__chevron me-1" :class="{'town-zoning-plan-map__chevron--open': proposedAreasOpen}">
                    <span class="fas fa-chevron-right"></span>
                </span>
                Seznam návrhových ploch
            </button>
            <div v-if="proposedAreasOpen" class="mt-2">
                <div v-if="proposedAreas === null && !proposedAreasFailed" class="small text-secondary">
                    <span class="spinner-border spinner-border-sm me-1" role="status"></span> Načítám návrhové plochy…
                </div>
                <div v-else-if="proposedAreasFailed" class="small text-secondary">Návrhové plochy se nepodařilo načíst.</div>
                <template v-else-if="proposedAreas !== null">
                    <div class="d-flex flex-wrap justify-content-between align-items-center column-gap-3 small mb-1">
                        <span class="text-secondary">
                            {{ filteredProposedAreas.length }} z {{ proposedAreas.length }} návrhových ploch{{ proposedAreasFilterLabel }},
                            od největší; kliknutím plochu ukážete v mapě.
                        </span>
                        <div class="form-check form-switch mb-0">
                            <input id="town-zoning-plan-only-in-view" v-model="onlyInView" type="checkbox" class="form-check-input">
                            <label for="town-zoning-plan-only-in-view" class="form-check-label">Jen ve výřezu mapy</label>
                        </div>
                    </div>
                    <div v-if="filteredProposedAreas.length === 0" class="small text-secondary">
                        {{ onlyInView ? 'Ve výřezu mapy nejsou žádné návrhové plochy' : 'Žádné návrhové plochy' }}{{ selectedKeys.length > 0 ? ' vybrané v legendě' : '' }}.
                    </div>
                    <div class="list-group town-zoning-plan-map__proposed-areas">
                        <button v-for="(area, index) in shownProposedAreas"
                                :key="index"
                                type="button"
                                class="list-group-item list-group-item-action d-flex align-items-center gap-2 py-1 px-2 small"
                                :class="{'town-zoning-plan-map__legend-row--selected': area === focusedArea}"
                                @click="focusProposedArea(area)">
                            <span class="town-zoning-plan-map__swatch" :style="{background: proposedAreaColor(area)}"></span>
                            <span class="flex-grow-1 text-truncate">
                                <strong v-if="area.code">{{ area.code }}</strong> {{ proposedAreaName(area) }}
                            </span>
                            <span class="text-nowrap text-secondary">{{ formatHectares(area.hectares) }} ha</span>
                        </button>
                    </div>
                    <button v-if="filteredProposedAreas.length > shownProposedAreas.length"
                            type="button"
                            class="btn btn-link btn-sm p-0 mt-1 text-decoration-none"
                            @click="shownProposedAreaCount += PROPOSED_AREAS_PAGE_SIZE">
                        Zobrazit dalších {{ Math.min(PROPOSED_AREAS_PAGE_SIZE, filteredProposedAreas.length - shownProposedAreas.length) }}
                    </button>
                </template>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import MapyCzBasicLayer from '@/components/maps/MapyCzBasicLayer.vue';
import MapyCzOrthoPhotoLayer from '@/components/maps/MapyCzOrtophotoLayer.vue';
import {
    DEFAULT_USAGE_COLOR,
    fetchAreas,
    findHitFeature,
    ANNULLED_COLOR,
    ANNULLED_EXPLANATION,
    ANNULLED_LEGEND_LABEL,
    isAnnulled,
    isLongLabel,
    landUseStyle,
    overlayStyle,
    planCurrencyTexts,
    popupContent,
    sourceLabel,
    toFeatureCollection,
    USAGE_COLORS,
    ZoningArea,
} from '@/components/maps/zoningPlanAreas';
import {LControlLayers, LControlZoom, LMap} from '@vue-leaflet/vue-leaflet';
import L from 'leaflet';
import {computed, defineComponent, onBeforeUnmount, PropType, ref, shallowRef, watch} from 'vue';

interface Coordinates {
    readonly latitude: number;
    readonly longitude: number;
}

interface LegendItem {
    readonly code: string | null;
    readonly originalLabel: string | null;
    readonly originalColor: string | null;
    readonly usageType: string | null;
    readonly usageTypeName: string | null;
    readonly existingHectares: number;
    readonly proposedHectares: number;
    readonly unknownStateHectares: number;
}

// TownProposedArea DTO of the backend.
interface ProposedArea {
    readonly code: string | null;
    readonly originalLabel: string | null;
    readonly originalColor: string | null;
    readonly usageType: string | null;
    readonly usageTypeName: string | null;
    readonly hectares: number;
    readonly boundingBox: {readonly southWest: Coordinates; readonly northEast: Coordinates};
    readonly interiorPoint: Coordinates;
}

// TownZoningPlanMap DTO of the backend.
interface TownMap {
    readonly townId: string;
    readonly boundingBox: {readonly southWest: Coordinates; readonly northEast: Coordinates};
    readonly loadsWholeTown: boolean;
    readonly geometrySource: string | null;
    readonly containedChangeName: string | null;
    readonly containedChangeEffectiveDate: string | null;
    readonly latestChangeName: string | null;
    readonly latestChangeEffectiveDate: string | null;
    readonly behindRegistry: boolean;
    readonly partialVectorization: boolean;
    readonly totalHectares: number;
    // The land-use areas a court or the region annulled, not in the legend.
    readonly annulledHectares: number;
    readonly legend: LegendItem[];
}

type Mode = 'all' | 'existing' | 'proposed';

interface ManagedLayer {
    readonly key: string;
    readonly geoJson: L.GeoJSON;
    loadedBounds: L.LatLngBounds | null;
    abortController: AbortController | null;
}

const LAND_USE = 'land_use';

// Short labels fit one row under the map; the full name is in the title.
const OVERLAY_LAYERS = [
    {key: 'built_up_area', label: 'Zastavěné území', title: 'Zastavěné území'},
    {key: 'public_benefit', label: 'Veřejně prospěšné stavby', title: 'Veřejně prospěšné stavby a opatření'},
    {key: 'ecological_stability', label: 'ÚSES', title: 'Územní systém ekologické stability'},
    {key: 'territorial_reserve', label: 'Územní rezervy', title: 'Územní rezervy'},
    {key: 'corridor', label: 'Koridory', title: 'Koridory dopravní a technické infrastruktury'},
];

const PANE_NAME = 'townZoningPlan';
const PANE_Z_INDEX = '240';
// The boundary of the town above the areas; non-interactive, so clicks reach the areas below it.
const BOUNDARY_PANE_NAME = 'townZoningPlanBoundary';
const BOUNDARY_PANE_Z_INDEX = '245';
const BOUNDARY_STYLE: L.PathOptions = {color: '#212121', weight: 2.5, opacity: 0.8, fill: false};
// The outline of a proposed area picked in the list, until its popup closes.
const FOCUS_STYLE: L.PathOptions = {color: '#ff6d00', weight: 3.5, opacity: 1, fill: false};
// A move to a proposed area from the list is animated, so it is clear where the map went.
const FOCUS_FLIGHT_SECONDS = 0.8;
// The list of proposed areas shows this many rows at once; big cities have thousands.
const PROPOSED_AREAS_PAGE_SIZE = 50;
const BOUNDS_PADDING = 0.25;
const FIT_PADDING: L.PointExpression = [16, 16];

// The big cities are loaded per viewport (see TownZoningPlanMapQueryHandler), like the big map.
const MIN_ZOOM = 14;

const DIMMED_COLOR = '#bdbdbd';
const HIGHLIGHT_COLOR = '#212121';

// Proposed areas of these usage types are named one by one in the summary of the proposal (where
// building is planned); the others — landscape, transport, infrastructure — are summed up.
const SUMMARY_USAGE_TYPES = ['residential', 'mixed_residential', 'civic_amenities', 'production', 'recreation'];

// Identifies a legend item on both sides: the backend groups the land-use areas by these fields.
function legendKey(code: string | null, label: string | null, color: string | null, usageType: string | null): string {
    return JSON.stringify([code, label, color, usageType]);
}

function areaLegendKey(area: ZoningArea): string {
    return legendKey(area.code, area.originalLabel, area.originalColor, area.usageType);
}

const numberFormat = new Intl.NumberFormat('cs-CZ', {minimumFractionDigits: 1, maximumFractionDigits: 1});

/**
 * The map of one town's zoning plan on its page: the whole town at once (the big cities per viewport),
 * the town's own legend with the hectares of each item, the current state and the proposed changes
 * apart, and the encumbrance layers on demand.
 */
export default defineComponent({
    components: {
        LMap,
        LControlLayers,
        LControlZoom,
        MapyCzBasicLayer,
        MapyCzOrthoPhotoLayer,
    },
    props: {
        map: {
            type: Object as PropType<TownMap>,
            required: true,
        },
        loadAreasUrl: {
            type: String,
            required: true,
        },
        loadBoundaryUrl: {
            type: String,
            required: true,
        },
        loadProposedAreasUrl: {
            type: String,
            required: true,
        },
        planDetailUrl: {
            type: String,
            required: true,
        },
        // 'true' for admins: technical ids and vectorization quality in the popup (map-side QA).
        debugInfo: {
            type: String,
            required: true,
        },
    },
    setup(props) {
        const mode = ref<Mode>('all');
        const selectedKeys = ref<string[]>([]);
        const hoveredKey = ref<string | null>(null);
        const fillOpacity = ref(0.5);
        const visibleOverlays = ref<string[]>([]);
        const loading = ref(false);
        const zoomTooSmall = ref(false);

        let leafletMap: L.Map | null = null;
        const managedLayers: ManagedLayer[] = [];
        let pendingRequests = 0;
        const boundaryAbortController = new AbortController();

        const townBounds = L.latLngBounds(
            [props.map.boundingBox.southWest.latitude, props.map.boundingBox.southWest.longitude],
            [props.map.boundingBox.northEast.latitude, props.map.boundingBox.northEast.longitude],
        );
        const initialCenter = townBounds.getCenter();

        const currency = planCurrencyTexts(props.map);

        const proposedHectares = props.map.legend.reduce((sum, item) => sum + item.proposedHectares, 0);
        const unknownStateHectares = props.map.legend.reduce((sum, item) => sum + item.unknownStateHectares, 0);
        // Most sources tell the current state from the proposed changes, PDF-vectorized plans never do:
        // without it the switch would pass all areas off as the current state and "no proposal".
        const distinguishesPlanState = props.map.legend.some((item) => item.existingHectares + item.proposedHectares > 0);

        // "bydlení 11,2 ha · výroba a skladování 3,1 ha · ostatní 12,6 ha" — from the legend, which carries
        // the proposed hectares of every item; null when the plan proposes nothing (or does not tell).
        const proposalSummary = ((): string | null => {
            if (!distinguishesPlanState || proposedHectares <= 0) {
                return null;
            }
            const byUsageType = new Map<string, {name: string, hectares: number}>();
            let otherHectares = 0;
            for (const item of props.map.legend) {
                if (item.proposedHectares <= 0) {
                    continue;
                }
                if (item.usageType === null || item.usageTypeName === null || !SUMMARY_USAGE_TYPES.includes(item.usageType)) {
                    otherHectares += item.proposedHectares;
                    continue;
                }
                const group = byUsageType.get(item.usageType) ?? {name: item.usageTypeName, hectares: 0};
                group.hectares += item.proposedHectares;
                byUsageType.set(item.usageType, group);
            }
            const parts = SUMMARY_USAGE_TYPES
                .map((usageType) => byUsageType.get(usageType))
                .filter((group): group is {name: string, hectares: number} => group !== undefined)
                .map((group) => `${group.name.charAt(0).toLocaleLowerCase('cs-CZ')}${group.name.slice(1)} ${formatHectares(group.hectares)} ha`);
            if (otherHectares > 0) {
                parts.push(`ostatní (krajina, doprava, infrastruktura) ${formatHectares(otherHectares)} ha`);
            }
            return parts.join(' · ');
        })();

        const modeOptions = computed(() => [
            {mode: 'all' as Mode, label: 'Vše'},
            {mode: 'existing' as Mode, label: 'Stav'},
            {mode: 'proposed' as Mode, label: `Návrh (${numberFormat.format(proposedHectares)} ha)`},
        ]);

        function hectaresInMode(item: LegendItem): number {
            switch (mode.value) {
                case 'existing':
                    return item.existingHectares;
                case 'proposed':
                    return item.proposedHectares;
                default:
                    return item.existingHectares + item.proposedHectares + item.unknownStateHectares;
            }
        }

        const legendRows = computed(() => props.map.legend
            .map((item) => {
                const label = item.originalLabel !== null && !isLongLabel(item.originalLabel) ? item.originalLabel : null;
                const name = label ?? item.usageTypeName ?? 'Plocha';
                return {
                    key: legendKey(item.code, item.originalLabel, item.originalColor, item.usageType),
                    code: item.code,
                    name,
                    title: [item.code, name, label !== null ? item.usageTypeName : null].filter(Boolean).join(' — '),
                    color: item.originalColor ?? USAGE_COLORS[item.usageType ?? ''] ?? DEFAULT_USAGE_COLOR,
                    hectares: hectaresInMode(item),
                };
            })
            .filter((row) => row.hectares > 0));

        function formatHectares(hectares: number): string {
            return hectares < 0.1 ? '< 0,1' : numberFormat.format(hectares);
        }

        function formatShare(hectares: number): string {
            if (props.map.totalHectares <= 0) {
                return '–';
            }
            const share = hectares / props.map.totalHectares * 100;
            return share < 0.1 ? '< 0,1 %' : `${numberFormat.format(share)} %`;
        }

        function toggleSelected(key: string): void {
            selectedKeys.value = selectedKeys.value.includes(key)
                ? selectedKeys.value.filter((selected) => selected !== key)
                : [...selectedKeys.value, key];
        }

        function isShown(area: ZoningArea): boolean {
            if (area.layer !== LAND_USE || mode.value === 'all') {
                return true;
            }
            // Areas of an unknown state belong to neither.
            return area.planState === mode.value;
        }

        function styleOf(area: ZoningArea): L.PathOptions {
            if (area.layer !== LAND_USE) {
                return overlayStyle(area);
            }
            if (!isShown(area)) {
                return {stroke: false, fill: false};
            }
            const style: L.PathOptions = {
                ...landUseStyle(area),
                stroke: true,
                fill: true,
                fillOpacity: isAnnulled(area) ? fillOpacity.value * 0.3 : fillOpacity.value,
            };
            const key = areaLegendKey(area);
            if (selectedKeys.value.length > 0 && !selectedKeys.value.includes(key)) {
                return {...style, fillColor: DIMMED_COLOR, fillOpacity: fillOpacity.value * 0.4};
            }
            if (hoveredKey.value === key) {
                return {...style, color: HIGHLIGHT_COLOR, weight: 2.5, fillOpacity: Math.min(1, fillOpacity.value + 0.25)};
            }
            return style;
        }

        function restyleLandUse(): void {
            managedLayers
                .find((managed) => managed.key === LAND_USE)
                ?.geoJson.setStyle((feature) => styleOf(feature?.properties as ZoningArea));
        }

        // The whole town at once, or (the big cities) the viewport once zoomed in enough.
        function viewToLoad(): L.LatLngBounds | null {
            if (props.map.loadsWholeTown) {
                return townBounds;
            }
            if (!leafletMap || leafletMap.getZoom() < MIN_ZOOM) {
                return null;
            }
            return leafletMap.getBounds();
        }

        async function loadLayer(managed: ManagedLayer): Promise<void> {
            if (!leafletMap || !leafletMap.hasLayer(managed.geoJson)) {
                return;
            }

            const view = viewToLoad();
            if (view === null) {
                managed.geoJson.clearLayers();
                managed.loadedBounds = null;
                return;
            }
            if (managed.loadedBounds && managed.loadedBounds.contains(view)) {
                return;
            }
            // A viewport is loaded with a margin, so moving the map within it needs no new request.
            const bounds = props.map.loadsWholeTown ? view : view.pad(BOUNDS_PADDING);

            managed.abortController?.abort();
            const abortController = new AbortController();
            managed.abortController = abortController;

            pendingRequests++;
            loading.value = true;
            try {
                const areas = await fetchAreas(props.loadAreasUrl, managed.key, bounds, abortController.signal, props.map.townId);
                if (areas === null) {
                    return;
                }
                managed.geoJson.clearLayers();
                managed.geoJson.addData(toFeatureCollection(areas));
                managed.loadedBounds = bounds;
            } catch (error) {
                if (!(error instanceof DOMException && error.name === 'AbortError')) {
                    managed.loadedBounds = null;
                }
            } finally {
                pendingRequests--;
                loading.value = pendingRequests > 0;
            }
        }

        // Settles once the layers requested by the last move are loaded (see focusProposedArea).
        let layersLoaded: Promise<unknown> = Promise.resolve();

        function loadVisibleLayers(): void {
            zoomTooSmall.value = !props.map.loadsWholeTown && leafletMap !== null && leafletMap.getZoom() < MIN_ZOOM;
            layersLoaded = Promise.all(managedLayers.map((managed) => loadLayer(managed)));
        }

        // The popup of all visible layers at the point; returns the land-use feature hit, if any.
        // isLandUseHittable narrows which land-use area the point may hit (a proposed area picked in the list).
        function openPopupAt(latlng: L.LatLng, isLandUseHittable: (area: ZoningArea) => boolean = isShown): GeoJSON.Feature | null {
            if (!leafletMap) {
                return null;
            }
            const contents: string[] = [];
            let landUseFeature: GeoJSON.Feature | null = null;
            for (const managed of managedLayers) {
                if (!leafletMap.hasLayer(managed.geoJson)) {
                    continue;
                }
                const feature = findHitFeature(leafletMap, managed.geoJson, latlng, managed.key === LAND_USE ? isLandUseHittable : isShown);
                if (feature?.properties) {
                    contents.push(popupContent(feature.properties as ZoningArea, props.planDetailUrl, props.debugInfo === 'true'));
                    if (managed.key === LAND_USE) {
                        landUseFeature = feature;
                    }
                }
            }
            if (contents.length > 0) {
                L.popup({className: 'zoning-plan-popup'})
                    .setLatLng(latlng)
                    .setContent(contents.join('<hr class="zoning-plan-popup__sep"/>'))
                    .openOn(leafletMap);
            }
            return landUseFeature;
        }

        function handleMapClick(event: L.LeafletMouseEvent): void {
            openPopupAt(event.latlng);
        }

        const proposedAreasOpen = ref(false);
        const proposedAreas = ref<ProposedArea[] | null>(null);
        const proposedAreasFailed = ref(false);
        let focusOutline: L.GeoJSON | null = null;

        // The list follows what the map shows: by default only the areas reaching into the view, and
        // only the legend items selected there.
        const onlyInView = ref(true);
        const viewBounds = shallowRef<L.LatLngBounds | null>(null);
        const shownProposedAreaCount = ref(PROPOSED_AREAS_PAGE_SIZE);

        const filteredProposedAreas = computed(() => (proposedAreas.value ?? []).filter((area) => {
            const key = legendKey(area.code, area.originalLabel, area.originalColor, area.usageType);
            if (selectedKeys.value.length > 0 && !selectedKeys.value.includes(key)) {
                return false;
            }
            return !onlyInView.value || viewBounds.value === null || viewBounds.value.intersects(L.latLngBounds(
                [area.boundingBox.southWest.latitude, area.boundingBox.southWest.longitude],
                [area.boundingBox.northEast.latitude, area.boundingBox.northEast.longitude],
            ));
        }));
        const shownProposedAreas = computed(() => filteredProposedAreas.value.slice(0, shownProposedAreaCount.value));
        const proposedAreasFilterLabel = computed(() => [
            onlyInView.value ? ' ve výřezu mapy' : '',
            selectedKeys.value.length > 0 ? ' vybraných v legendě' : '',
        ].join(''));

        watch([onlyInView, viewBounds, selectedKeys], () => {
            shownProposedAreaCount.value = PROPOSED_AREAS_PAGE_SIZE;
        });

        function updateViewBounds(): void {
            if (movingToArea) {
                movingToArea = false;
                return;
            }
            viewBounds.value = leafletMap?.getBounds() ?? null;
        }

        // The list is loaded on the first opening only: the hectares of all proposed areas are not
        // computed for every page view.
        async function toggleProposedAreas(): Promise<void> {
            proposedAreasOpen.value = !proposedAreasOpen.value;
            if (!proposedAreasOpen.value || proposedAreas.value !== null) {
                return;
            }
            proposedAreasFailed.value = false;
            try {
                const response = await fetch(props.loadProposedAreasUrl);
                if (!response.ok) {
                    proposedAreasFailed.value = true;
                    return;
                }
                proposedAreas.value = await response.json();
            } catch {
                proposedAreasFailed.value = true;
            }
        }

        function proposedAreaName(area: ProposedArea): string {
            return area.originalLabel !== null && !isLongLabel(area.originalLabel)
                ? area.originalLabel
                : area.usageTypeName ?? 'Plocha';
        }

        function proposedAreaColor(area: ProposedArea): string {
            return area.originalColor ?? USAGE_COLORS[area.usageType ?? ''] ?? DEFAULT_USAGE_COLOR;
        }

        function clearFocusOutline(): void {
            focusOutline?.remove();
            focusOutline = null;
        }

        const focusedArea = shallowRef<ProposedArea | null>(null);
        // Views left by moves to proposed areas that the browser history can return to: each move numbers
        // its history entry, so back and forward both land on the right count.
        const HISTORY_INDEX_KEY = 'townZoningPlanMapView';
        const historyIndexOf = (state: unknown): number => {
            const index = (state as Record<string, unknown> | null)?.[HISTORY_INDEX_KEY];
            return typeof index === 'number' ? index : 0;
        };
        const returnableViews = ref(historyIndexOf(window.history.state));
        // The next end of a move comes from a move to a proposed area: the list keeps its view, so its
        // rows do not reshuffle under the cursor; it follows the map again once the user moves it.
        let movingToArea = false;

        // Never closer than the user is (only further out when the area does not fit), and for the big
        // cities at least as close as their areas are drawn.
        function zoomToShow(bounds: L.LatLngBounds): number {
            if (!leafletMap) {
                return 0;
            }
            const zoom = Math.min(leafletMap.getZoom(), leafletMap.getBoundsZoom(bounds, false, L.point(FIT_PADDING)));
            return props.map.loadsWholeTown ? zoom : Math.max(zoom, MIN_ZOOM);
        }

        function moveTo(center: L.LatLng, zoom: number): Promise<void> {
            return new Promise((resolve) => {
                if (!leafletMap) {
                    resolve();
                    return;
                }
                leafletMap.once('moveend', () => resolve());
                // Should the flight end without a move event, the popup still opens.
                window.setTimeout(resolve, (FOCUS_FLIGHT_SECONDS + 1) * 1000);
                leafletMap.flyTo(center, zoom, {duration: FOCUS_FLIGHT_SECONDS});
            });
        }

        // Outlines the area and opens its popup at a point inside it; the map moves only when the area is
        // not in the view, and a history entry lets the browser's back button return to the view left.
        async function focusProposedArea(area: ProposedArea): Promise<void> {
            if (!leafletMap) {
                return;
            }
            focusedArea.value = area;
            if (mode.value === 'existing') {
                mode.value = 'all';
            }
            clearFocusOutline();
            leafletMap.closePopup();
            leafletMap.getContainer().scrollIntoView({behavior: 'smooth', block: 'nearest'});

            const bounds = L.latLngBounds(
                [area.boundingBox.southWest.latitude, area.boundingBox.southWest.longitude],
                [area.boundingBox.northEast.latitude, area.boundingBox.northEast.longitude],
            );
            const zoom = zoomToShow(bounds);
            if (!leafletMap.getBounds().contains(bounds) || zoom !== leafletMap.getZoom()) {
                writeSharedState();
                returnableViews.value++;
                window.history.pushState({...window.history.state, [HISTORY_INDEX_KEY]: returnableViews.value}, '', window.location.hash);
                movingToArea = true;
                await moveTo(bounds.getCenter(), zoom);
                movingToArea = false;
            }
            await layersLoaded;
            if (!leafletMap || focusedArea.value !== area) {
                return;
            }
            // Only the picked area: a smaller current-state area may overlap it at its interior point.
            const key = legendKey(area.code, area.originalLabel, area.originalColor, area.usageType);
            const feature = openPopupAt(
                L.latLng(area.interiorPoint.latitude, area.interiorPoint.longitude),
                (candidate) => isShown(candidate) && candidate.planState === 'proposed' && areaLegendKey(candidate) === key,
            );
            if (feature !== null) {
                focusOutline = L.geoJSON(feature, {pane: BOUNDARY_PANE_NAME, interactive: false, style: FOCUS_STYLE}).addTo(leafletMap);
                leafletMap.once('popupclose', clearFocusOutline);
            }
        }

        function returnToPreviousView(): void {
            window.history.back();
        }

        // The browser's back (and forward) button moves the map to the view of the history entry.
        function onHistoryMove(event: PopStateEvent): void {
            // Another fragment of the page (a "#" link) is no view of the map.
            if (!new URLSearchParams(window.location.hash.slice(1)).has('mapa')) {
                return;
            }
            returnableViews.value = historyIndexOf(event.state);
            const view = readSharedState();
            if (view !== null && leafletMap) {
                clearFocusOutline();
                leafletMap.closePopup();
                void moveTo(view.center, view.zoom);
            }
        }

        function syncOverlays(): void {
            if (!leafletMap) {
                return;
            }
            for (const managed of managedLayers) {
                if (managed.key === LAND_USE) {
                    continue;
                }
                const visible = visibleOverlays.value.includes(managed.key);
                if (visible && !leafletMap.hasLayer(managed.geoJson)) {
                    managed.geoJson.addTo(leafletMap);
                } else if (!visible && leafletMap.hasLayer(managed.geoJson)) {
                    managed.abortController?.abort();
                    leafletMap.removeLayer(managed.geoJson);
                }
            }
            loadVisibleLayers();
        }

        // Loaded apart from the page, which the RUIAN API would otherwise delay; without it — also when
        // the request fails — the map simply shows no outline.
        async function loadBoundary(): Promise<void> {
            try {
                const response = await fetch(props.loadBoundaryUrl, {signal: boundaryAbortController.signal});
                const boundary: GeoJSON.Geometry | null = response.ok ? await response.json() : null;
                if (boundary === null || !leafletMap) {
                    return;
                }
                L.geoJSON(boundary, {pane: BOUNDARY_PANE_NAME, interactive: false, style: BOUNDARY_STYLE}).addTo(leafletMap);
            } catch {
                // No outline.
            }
        }

        function onMapReady(mapInstance: L.Map): void {
            leafletMap = mapInstance;
            if (sharedView !== null) {
                leafletMap.setView(sharedView.center, sharedView.zoom);
            } else {
                leafletMap.fitBounds(townBounds, {padding: FIT_PADDING});
            }

            const pane = leafletMap.createPane(PANE_NAME);
            pane.style.zIndex = PANE_Z_INDEX;
            const boundaryPane = leafletMap.createPane(BOUNDARY_PANE_NAME);
            boundaryPane.style.zIndex = BOUNDARY_PANE_Z_INDEX;
            boundaryPane.style.pointerEvents = 'none';

            for (const key of [LAND_USE, ...OVERLAY_LAYERS.map((overlay) => overlay.key)]) {
                // Non-interactive (clicks are handled at the map level, see handleMapClick) — keeps the
                // thousands of polygons light and lets the popup combine all layers at the clicked point.
                const geoJson = L.geoJSON(undefined, {
                    pane: PANE_NAME,
                    interactive: false,
                    style: (feature) => styleOf(feature?.properties as ZoningArea),
                });
                managedLayers.push({key, geoJson, loadedBounds: null, abortController: null});
            }
            managedLayers[0].geoJson.addTo(leafletMap);

            leafletMap.on('moveend', loadVisibleLayers);
            leafletMap.on('moveend', writeSharedState);
            leafletMap.on('moveend', updateViewBounds);
            updateViewBounds();
            window.addEventListener('popstate', onHistoryMove);
            leafletMap.on('click', handleMapClick);
            loadVisibleLayers();
            void loadBoundary();
        }

        // The view, mode and legend selection travel in the address ("#mapa=15/49.12345/16.12345&rezim=navrh
        // &vyber=0.3"), so a link leads to the same place of the plan; the selection by legend position.
        const MODE_NAMES: Record<Mode, string> = {all: 'vse', existing: 'stav', proposed: 'navrh'};
        const legendKeys = props.map.legend.map((item) => legendKey(item.code, item.originalLabel, item.originalColor, item.usageType));

        function readSharedState(): {center: L.LatLng, zoom: number} | null {
            let parameters: URLSearchParams;
            try {
                parameters = new URLSearchParams(window.location.hash.slice(1));
            } catch {
                return null;
            }
            const sharedMode = (Object.keys(MODE_NAMES) as Mode[]).find((key) => MODE_NAMES[key] === parameters.get('rezim'));
            if (sharedMode !== undefined && distinguishesPlanState) {
                mode.value = sharedMode;
            }
            selectedKeys.value = (parameters.get('vyber') ?? '')
                .split('.')
                .filter((index) => /^\d+$/.test(index))
                .map((index) => legendKeys[Number(index)])
                .filter((key): key is string => key !== undefined);

            const view = parameters.get('mapa')?.split('/') ?? [];
            const [zoom, latitude, longitude] = view.map(Number);
            if (view.length !== 3 || view.some((value) => value.trim() === '') || [zoom, latitude, longitude].some((value) => !Number.isFinite(value))) {
                return null;
            }
            return {center: L.latLng(latitude, longitude), zoom};
        }

        function writeSharedState(): void {
            if (!leafletMap) {
                return;
            }
            const center = leafletMap.getCenter();
            const parameters = new URLSearchParams();
            parameters.set('mapa', `${leafletMap.getZoom()}/${center.lat.toFixed(5)}/${center.lng.toFixed(5)}`);
            if (mode.value !== 'all') {
                parameters.set('rezim', MODE_NAMES[mode.value]);
            }
            const selection = selectedKeys.value.map((key) => legendKeys.indexOf(key)).filter((index) => index >= 0);
            if (selection.length > 0) {
                parameters.set('vyber', selection.join('.'));
            }
            // Decoded, so the address stays readable ("15/49.12345/16.12345").
            window.history.replaceState(window.history.state, '', `#${decodeURIComponent(parameters.toString())}`);
        }

        const sharedView = readSharedState();

        watch([mode, selectedKeys], writeSharedState);
        watch([mode, selectedKeys, hoveredKey, fillOpacity], restyleLandUse);
        watch(visibleOverlays, syncOverlays);

        onBeforeUnmount(() => {
            boundaryAbortController.abort();
            window.removeEventListener('popstate', onHistoryMove);
            if (leafletMap) {
                leafletMap.off('moveend', loadVisibleLayers);
                leafletMap.off('moveend', writeSharedState);
                leafletMap.off('moveend', updateViewBounds);
                leafletMap.off('click', handleMapClick);
            }
            for (const managed of managedLayers) {
                managed.abortController?.abort();
            }
            managedLayers.length = 0;
            leafletMap = null;
        });

        return {
            initialCenter,
            mode,
            modeOptions,
            proposalSummary,
            proposedHectares,
            distinguishesPlanState,
            unknownStateHectares,
            legendRows,
            selectedKeys,
            hoveredKey,
            fillOpacity,
            visibleOverlays,
            overlayLayers: OVERLAY_LAYERS,
            loading,
            zoomTooSmall,
            onMapReady,
            proposedAreasOpen,
            proposedAreas,
            proposedAreasFailed,
            onlyInView,
            filteredProposedAreas,
            shownProposedAreas,
            shownProposedAreaCount,
            proposedAreasFilterLabel,
            PROPOSED_AREAS_PAGE_SIZE,
            toggleProposedAreas,
            proposedAreaName,
            proposedAreaColor,
            focusProposedArea,
            focusedArea,
            returnableViews,
            returnToPreviousView,
            toggleSelected,
            formatHectares,
            annulledColor: ANNULLED_COLOR,
            annulledExplanation: ANNULLED_EXPLANATION,
            annulledLegendLabel: ANNULLED_LEGEND_LABEL,
            formatShare,
            currency,
            sourceLabel,
        };
    },
});
</script>
